"use client";

import React, { useState, useEffect, useRef } from "react";
import { Zap, AlertTriangle, CheckCircle2, Activity } from "lucide-react";

interface Preset {
  label: string;
  operations: number;
  description: string;
}

const PRESETS: Preset[] = [
  {
    label: "Leichte Last (50 Mio.)",
    operations: 50_000_000,
    description: "Typische Datenfilterung – kurzer Ruckler im Main Thread.",
  },
  {
    label: "Mittlere Last (250 Mio.)",
    operations: 250_000_000,
    description: "Komplexes Parsing – spürbarer UI-Freeze von ca. 1 Sekunde.",
  },
  {
    label: "Extremer Stresstest (800 Mio.)",
    operations: 800_000_000,
    description: "Große mathematische Berechnung – voller Main-Thread-Lock.",
  },
];

export default function PerformancePlayground() {
  const [selectedPreset, setSelectedPreset] = useState<Preset>(PRESETS[1]);
  const [clickCount, setClickCount] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [mode, setMode] = useState<"idle" | "main" | "worker">("idle");
  const [duration, setDuration] = useState<number | null>(null);
  const [result, setResult] = useState<number | null>(null);

  // Live Heartbeat Ticker to visibly detect UI freezes
  const [ticker, setTicker] = useState(0);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    let lastTime = performance.now();
    const loop = (currentTime: number) => {
      if (currentTime - lastTime >= 50) {
        setTicker((t) => (t + 1) % 100);
        lastTime = currentTime;
      }
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  const heavyCalculation = (ops: number): number => {
    let sum = 0;
    for (let i = 0; i < ops; i++) {
      sum += Math.sqrt(i);
    }
    return sum;
  };

  const runMainThread = () => {
    setIsCalculating(true);
    setMode("main");
    setDuration(null);
    setResult(null);

    // Timeout allows the UI to paint the loading state right before freezing
    setTimeout(() => {
      const start = performance.now();
      const calcResult = heavyCalculation(selectedPreset.operations);
      const end = performance.now();

      setResult(calcResult);
      setDuration(Math.round(end - start));
      setIsCalculating(false);
    }, 40);
  };

  const runWebWorker = () => {
    setIsCalculating(true);
    setMode("worker");
    setDuration(null);
    setResult(null);

    const start = performance.now();
    const workerScript = `
      self.onmessage = function(e) {
        var ops = e.data;
        var sum = 0;
        for (var i = 0; i < ops; i++) {
          sum += Math.sqrt(i);
        }
        self.postMessage(sum);
      };
    `;

    const blob = new Blob([workerScript], { type: "application/javascript" });
    const blobUrl = URL.createObjectURL(blob);
    const worker = new Worker(blobUrl);

    worker.onmessage = (e) => {
      const end = performance.now();
      setResult(e.data);
      setDuration(Math.round(end - start));
      setIsCalculating(false);
      worker.terminate();
      URL.revokeObjectURL(blobUrl);
    };

    worker.onerror = () => {
      setIsCalculating(false);
      worker.terminate();
      URL.revokeObjectURL(blobUrl);
    };

    worker.postMessage(selectedPreset.operations);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Preset Selector */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          Last-Szenario wählen (Presets):
        </span>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {PRESETS.map((p) => {
            const isActive = selectedPreset.operations === p.operations;
            return (
              <button
                key={p.operations}
                type="button"
                onClick={() => {
                  setSelectedPreset(p);
                  setDuration(null);
                  setResult(null);
                }}
                disabled={isCalculating}
                className={`flex flex-col items-start rounded-xl border p-3 text-left transition-all cursor-pointer ${
                  isActive
                    ? "border-blue-500 bg-blue-50/50 shadow-xs dark:border-blue-500/60 dark:bg-blue-950/30"
                    : "border-neutral-200/90 bg-neutral-50/60 hover:bg-neutral-100/80 dark:border-neutral-800 dark:bg-neutral-900/40 dark:hover:bg-neutral-800/60"
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <div className="flex items-center gap-1.5 font-heading text-xs font-bold text-neutral-900 dark:text-white">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      isActive ? "bg-blue-600 dark:bg-blue-400" : "bg-neutral-400 dark:bg-neutral-600"
                    }`}
                  />
                  {p.label}
                </div>
                <span className="mt-1 text-[11px] leading-tight text-neutral-500 dark:text-neutral-400">
                  {p.description}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Responsiveness Test Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-900/60">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setClickCount((c) => c + 1)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-xs transition-all hover:bg-blue-700 active:scale-95 cursor-pointer dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            <Activity className="h-3.5 w-3.5" />
            UI-Klicktest (Klicks: {clickCount})
          </button>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            Tippe während des Tests hierauf, um Verzögerungen sofort zu spüren.
          </span>
        </div>

        {/* Live Event-Loop Pulse */}
        <div className="flex items-center gap-2 font-mono text-xs">
          <span className="text-neutral-500 dark:text-neutral-400">Main-Thread-Puls:</span>
          <span className="inline-flex h-6 w-12 items-center justify-center rounded-md border border-neutral-200 bg-white font-semibold text-neutral-800 shadow-2xs dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
            {ticker}
          </span>
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full transition-colors ${
              isCalculating && mode === "main"
                ? "bg-red-500 shadow-sm shadow-red-500/50"
                : "bg-emerald-500"
            }`}
            title={isCalculating && mode === "main" ? "Thread blockiert!" : "Event-Loop aktiv"}
          />
        </div>
      </div>

      {/* Benchmark Action Columns */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Main Thread Card */}
        <div className="flex flex-col justify-between rounded-xl border border-amber-200/80 bg-amber-50/40 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="font-heading text-sm font-bold text-amber-900 dark:text-amber-200">
                1. Main Thread (Synchron)
              </span>
              <span className="rounded-full bg-amber-200/60 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/60 dark:text-amber-300">
                Blockierend
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-amber-800/90 dark:text-amber-300/80">
              Führt die Schleife direkt im UI-Thread aus. Der gesamte Browser-Tab friert für die
              Dauer der Berechnung ein: kein Scrollen, kein Puls, keine Reaktion auf Klicks.
            </p>
          </div>

          <button
            type="button"
            onClick={runMainThread}
            disabled={isCalculating}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer dark:bg-amber-500 dark:hover:bg-amber-600"
          >
            <AlertTriangle className="h-4 w-4" />
            Synchron im Main Thread ausführen
          </button>
        </div>

        {/* Web Worker Card */}
        <div className="flex flex-col justify-between rounded-xl border border-emerald-200/80 bg-emerald-50/40 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20">
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="font-heading text-sm font-bold text-emerald-900 dark:text-emerald-200">
                2. Web Worker (Hintergrund)
              </span>
              <span className="rounded-full bg-emerald-200/60 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300">
                60 FPS Fluid
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-emerald-800/90 dark:text-emerald-300/80">
              Lagert die Rechenlast auf einen separaten OS-Thread aus. Der Main Thread bleibt
              vollständig frei: Animationen laufen ununterbrochen und Klicks reagieren in &lt; 16ms.
            </p>
          </div>

          <button
            type="button"
            onClick={runWebWorker}
            disabled={isCalculating}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer dark:bg-emerald-500 dark:hover:bg-emerald-600"
          >
            <Zap className="h-4 w-4" />
            Parallel im Web Worker ausführen
          </button>
        </div>
      </div>

      {/* Results / Status Panel (CLS stable with fixed min-height) */}
      <div className="flex min-h-[92px] items-center justify-center rounded-xl border border-neutral-200/90 bg-white p-4 shadow-2xs dark:border-neutral-800 dark:bg-neutral-900/70">
        {isCalculating ? (
          <div className="flex items-center gap-3 text-neutral-600 dark:text-neutral-300">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400" />
            <span className="text-xs font-medium">
              {mode === "main"
                ? "Berechnung blockiert den Main Thread (UI eingefroren)..."
                : "Berechnung läuft im Web Worker (UI bleibt reaktionsfähig)..."}
            </span>
          </div>
        ) : duration !== null && result !== null ? (
          <div className="flex flex-wrap items-center justify-around gap-6 text-center">
            <div>
              <span className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Ausführungsdauer
              </span>
              <div className="font-heading text-lg font-bold text-neutral-900 dark:text-white">
                {duration} ms
              </div>
            </div>

            <div>
              <span className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Architektur-Modus
              </span>
              <div
                className={`font-heading text-xs font-bold ${
                  mode === "worker"
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-amber-600 dark:text-amber-400"
                }`}
              >
                {mode === "worker" ? "Web Worker (Non-blocking)" : "Main Thread (Frozen UI)"}
              </div>
            </div>

            <div>
              <span className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                UI-Status während Ausführung
              </span>
              <div className="flex items-center justify-center gap-1 font-heading text-xs font-semibold text-neutral-800 dark:text-neutral-200">
                {mode === "worker" ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    <span>Flüssig (60 FPS erhalten)</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    <span>Blockiert ({duration}ms Stillstand)</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            Wähle ein Last-Preset und starte den Vergleich, um den Unterschied live zu messen.
          </span>
        )}
      </div>
    </div>
  );
}
